import {
  AlertTriangle,
  CheckCircle,
  Circle,
  Info,
  Loader,
  Lock,
  type LucideIcon,
  XCircle,
} from "lucide-react";

import {
  IgBorderColors,
  IgCoreTextColors,
  IgIconColors,
} from "@intelligems/ig-design-tokens";

import { gateSpec, type WorkspaceGatesConfig } from "../../domainConfig";
import {
  formatFailedChecks,
  formatGenericDetails,
  formatTestSuiteFailures,
} from "../../gateDetails";
import type { WorkspaceGateResult, WorkspaceGateStatus } from "../../types";

/**
 * A `passed` gate whose result matches the domain's sentinel means "nothing to
 * run", not "your tests passed" — surfaced as an informational warning so the
 * UI never overclaims.
 */
export const isSentinelPass = <GateKey extends string>(
  gate: WorkspaceGateResult<GateKey>,
  config: WorkspaceGatesConfig<GateKey>
): boolean => {
  const sentinel = config.noUserTestsSentinel;
  return (
    sentinel !== null &&
    gate.gateKey === sentinel.gateKey &&
    gate.status === "passed" &&
    typeof gate.result === "string" &&
    gate.result.trim() === sentinel.result
  );
};

/**
 * Status → icon mapping. `fg` drives the icon and label colour, `spin` flags
 * the running indicator. The surrounding surface is tinted per *run*, not per
 * gate — see {@link resolveStripTone}.
 */
export type GateStatusStyle = {
  Icon: LucideIcon;
  fg: string;
  spin?: boolean;
};

export const GATE_STATUS_STYLE: Record<WorkspaceGateStatus, GateStatusStyle> = {
  passed: {
    Icon: CheckCircle,
    fg: IgCoreTextColors.tonal.success,
  },
  running: {
    Icon: Loader,
    fg: IgCoreTextColors.accent,
    spin: true,
  },
  failed: {
    Icon: XCircle,
    fg: IgCoreTextColors.tonal.error,
  },
  warning: {
    Icon: AlertTriangle,
    fg: IgCoreTextColors.tonal.warning,
  },
  pending: {
    Icon: Circle,
    fg: IgCoreTextColors.tertiary,
  },
  blocked: {
    Icon: Lock,
    fg: IgCoreTextColors.tertiary,
  },
};

const SENTINEL_STYLE: GateStatusStyle = {
  Icon: AlertTriangle,
  fg: IgCoreTextColors.tonal.info,
};

export const resolveGateStyle = <GateKey extends string>(
  gate: WorkspaceGateResult<GateKey>,
  config: WorkspaceGatesConfig<GateKey>
): GateStatusStyle =>
  isSentinelPass(gate, config)
    ? SENTINEL_STYLE
    : GATE_STATUS_STYLE[gate.status];

/** Human-readable status word. */
export const GATE_STATUS_LABEL: Record<WorkspaceGateStatus, string> = {
  passed: "Passed",
  running: "Running",
  failed: "Failed",
  warning: "Warning",
  pending: "Pending",
  blocked: "Blocked",
};

export const formatGateResult = (result: string): string => {
  const raw = result.trim();
  if (!raw) {
    return "No result details yet";
  }

  const normalized = raw.replace(/[-_]/g, " ").replace(/\s+/g, " ");
  const lower = normalized.toLowerCase();

  if (lower === "ok" || lower === "passed") {
    return "Passed";
  }
  if (lower === "review") {
    return "Review required";
  }
  if (lower === "running") {
    return "In progress";
  }

  return normalized[0].toUpperCase() + normalized.slice(1);
};

/** The gate's full name, from the domain's own gate order. */
export const gateLabel = <GateKey extends string>(
  gateKey: GateKey,
  config: WorkspaceGatesConfig<GateKey>
): string => gateSpec(config, gateKey)?.fullLabel ?? gateKey;

/**
 * What the whole run is doing right now, in one value.
 *
 * The progress line has to answer "how far along, and on what" without the
 * reader counting pills, so the derivation lives here rather than in render
 * code: one place decides what "failed" outranks, and the strip and its tests
 * read the same answer.
 */
export type GateRunPhase =
  | "idle"
  | "running"
  /**
   * A repair loop owns this run: the last iteration's failures are on
   * screen, but they are being worked on, not final. Ranked between
   * `running` and `failed` — calling the run "failed" while the loop is
   * mid-fix reads as terminal and is a lie.
   */
  | "repairing"
  /**
   * The entity this run belongs to has reached a terminal state that the run
   * does not describe — a rule Atlas gave up on, for instance. The gate rows
   * are the last thing that ran, but they are no longer a verdict on the
   * entity, so the strip must not report readiness. Outranks every settled
   * phase; a fresh run in flight outranks it in turn.
   */
  | "abandoned"
  | "failed"
  | "needs_review"
  | "passed";

export interface GateRunSummary<GateKey extends string> {
  phase: GateRunPhase;
  total: number;
  /** Gates that have reached a verdict. Drives "N of M". */
  settled: number;
  /** The gate in flight, or `null` when nothing is running. */
  current: WorkspaceGateResult<GateKey> | null;
  failed: WorkspaceGateResult<GateKey>[];
  /** Warnings and sentinel passes — "ran, but do not read this as a green". */
  needsReview: WorkspaceGateResult<GateKey>[];
}

const isSettled = (status: WorkspaceGateStatus) =>
  status === "passed" || status === "failed" || status === "warning";

interface GatePhaseInput {
  isPublishing: boolean;
  isRunning: boolean;
  isRepairing: boolean;
  isAbandoned: boolean;
  failedCount: number;
  needsReviewCount: number;
  settled: number;
  total: number;
}

/**
 * The phases in precedence order.
 *
 * A run in flight outranks everything: gates that have not been reached yet
 * are not verdicts, and calling the run "failed" before it finishes would
 * report a result the run has not produced.
 */
const resolvePhase = ({
  isPublishing,
  isRunning,
  isRepairing,
  isAbandoned,
  failedCount,
  needsReviewCount,
  settled,
  total,
}: GatePhaseInput): GateRunPhase => {
  if (isPublishing || isRunning) {
    return "running";
  }
  if (isRepairing) {
    return "repairing";
  }
  if (isAbandoned) {
    return "abandoned";
  }
  if (failedCount > 0) {
    return "failed";
  }
  if (settled === 0) {
    return "idle";
  }
  if (needsReviewCount > 0) {
    return "needs_review";
  }
  if (settled === total) {
    return "passed";
  }
  return "idle";
};

export const summarizeGates = <GateKey extends string>(
  gates: readonly WorkspaceGateResult<GateKey>[],
  config: WorkspaceGatesConfig<GateKey>,
  isPublishing: boolean,
  isRepairing = false,
  isAbandoned = false
): GateRunSummary<GateKey> => {
  const current = gates.find((gate) => gate.status === "running") ?? null;
  const failed = gates.filter(
    (gate) => gate.status === "failed" || gate.status === "blocked"
  );
  const needsReview = gates.filter(
    (gate) => gate.status === "warning" || isSentinelPass(gate, config)
  );
  const settled = gates.filter((gate) => isSettled(gate.status)).length;

  const phase = resolvePhase({
    isPublishing,
    isRunning: current !== null,
    isRepairing,
    isAbandoned,
    failedCount: failed.length,
    needsReviewCount: needsReview.length,
    settled,
    total: gates.length,
  });

  return { phase, total: gates.length, settled, current, failed, needsReview };
};

/**
 * The surface tint the strip wears.
 *
 * Tone is a function of the phase AND of whether a publish is on offer,
 * because the same verdict means two different things depending on what is
 * left to do: "all checks passed" beside a Publish button is an invitation and
 * earns a green surface, while the identical verdict on a version that is
 * already live is history and has no business colouring the page.
 *
 * Runs in flight stay plain on purpose — tinting them makes the bar flash
 * through three colours on the way to its verdict.
 */
export type GateStripTone = "success" | "error" | "warning" | "info" | "plain";

export const resolveStripTone = (
  phase: GateRunPhase,
  canPublish: boolean
): GateStripTone => {
  switch (phase) {
    case "failed":
      return "error";
    case "needs_review":
      return "warning";
    case "passed":
      return canPublish ? "success" : "plain";
    case "idle":
      return canPublish ? "info" : "plain";
    case "running":
    case "repairing":
    case "abandoned":
      return "plain";
  }
};

export interface GateToneColors {
  /** The strip's border. The surface stays plain paper in every tone. */
  border: string;
  /** Palette key the strip's action buttons take, so they read as one bar. */
  button: "success" | "error" | "warning" | "info";
  /** The headline icon, so the glyph agrees with the border around it. */
  icon: string;
  /**
   * Set where the phase's own glyph is wrong for the tone: an `idle` run is
   * grey-circle "pending" on a plain bar, but on a blue bar it is the same
   * informational notice IgAlert draws with `Info`.
   */
  Icon?: LucideIcon;
}

/**
 * The same border and icon tokens `IgAlert` uses for each severity
 * (`packages/ig-theme/src/IgAlert.tsx`), so a tinted strip and an alert on the
 * same page are the same blue, green and red. Read straight off the token
 * constants rather than the theme, matching how the rest of this module (and
 * the strip's own row styles) resolve colour.
 *
 * `plain` has no entry: it means "leave IgPaper's own border and the buttons'
 * own defaults alone", which is a different instruction from "paint these".
 */
export const GATE_TONE_COLORS: Record<
  Exclude<GateStripTone, "plain">,
  GateToneColors
> = {
  success: {
    border: IgBorderColors.success.default,
    button: "success",
    icon: IgIconColors.success,
  },
  error: {
    border: IgBorderColors.error.default,
    button: "error",
    icon: IgIconColors.error,
  },
  warning: {
    border: IgBorderColors.warning.default,
    button: "warning",
    icon: IgIconColors.warning,
  },
  info: {
    border: IgBorderColors.info.default,
    button: "info",
    icon: IgIconColors.info,
    Icon: Info,
  },
};

/**
 * A row's detail text, split into what the collapsed row can show and what the
 * reader has to open the row to see.
 */
export interface GateDetail {
  /** Single line rendered on the row itself. */
  summary: string;
  /** The detail in full, when the row's one line does not carry it. */
  body: string | null;
}

/**
 * Beyond roughly this many characters the row's single line ellipses away the
 * part of a failure the reader actually needs — the guard message, the failing
 * assertion — so anything longer also belongs in the expandable body.
 */
const ROW_DETAIL_FIT_CHARS = 72;

const normalize = (text: string): string =>
  text.replace(/\s+/g, " ").trim().toLowerCase();

/**
 * Everything the gate's own `detailsJson` says that the row line does not.
 *
 * A failed gate's `result` is a headline — "1 required check needs attention",
 * "3 tests failed" — and the thing the reader needs next (which check, which
 * assertion, what the runner printed) only exists in the structured details.
 * The domain's `formatDetails` is deliberately not consulted here: it is
 * written to fit the one-line row and already supplies the summary.
 */
const structuredDetails = <GateKey extends string>(
  gate: WorkspaceGateResult<GateKey>,
  config: WorkspaceGatesConfig<GateKey>
): string | null => {
  const details = gate.detailsJson;
  if (!details) {
    return null;
  }

  return (
    formatFailedChecks(details.failing) ??
    formatTestSuiteFailures(details.failures, config.testFailureTitlePrefix) ??
    // Last resort, and only for a gate that did not pass: a key/value dump is
    // worth reading when something went wrong and noise on a green row.
    (gate.status === "passed" ? null : formatGenericDetails(details))
  );
};

/**
 * What one gate row says, collapsed and expanded.
 *
 * A gate's verdict arrives as a free-form `result` string (for a failure, the
 * thrown error's own message) plus an optional structured `detailsJson`. The
 * row can only show one line of that, which is enough for "12 tests passed"
 * and useless for a guard rejection that names the construct and the fix, or
 * for a headline that merely counts the checks that failed. Both readings are
 * derived here so the row renders them and the tests assert them from one
 * place.
 */
export const describeGateDetail = <GateKey extends string>(
  gate: WorkspaceGateResult<GateKey>,
  config: WorkspaceGatesConfig<GateKey>
): GateDetail => {
  const spec = gateSpec(config, gate.gateKey);
  const formatted =
    spec?.formatDetails && gate.detailsJson
      ? spec.formatDetails(gate.detailsJson, gate)
      : null;
  const raw = typeof gate.result === "string" ? gate.result.trim() : "";

  const summary =
    formatted ?? (raw ? formatGateResult(raw) : GATE_STATUS_LABEL[gate.status]);

  // The raw message leads: it is the verdict as the runner reported it,
  // newlines and stack included. The structured details follow, so the body
  // reads "what happened" then "on what".
  const structured = structuredDetails(gate, config);
  const full = [
    raw || null,
    structured && !raw.includes(structured) ? structured : null,
  ]
    .filter((part): part is string => part !== null)
    .join("\n\n");

  if (!full) {
    return { summary, body: null };
  }

  // Worth opening only when the row cannot already show it: either the text
  // overflows the line, or it says something the summary does not.
  const body =
    full.includes("\n") ||
    full.length > ROW_DETAIL_FIT_CHARS ||
    !normalize(summary).includes(normalize(full))
      ? full
      : null;

  return { summary, body };
};

/** "Initial checks" for the baseline run, "Attempt N" for a repair iteration. */
export const attemptLabel = (attempt: number): string =>
  attempt === 0 ? "Initial checks" : `Attempt ${attempt}`;

/** One refused attempt: the gate that refused it, and which attempt it was. */
export interface GateFailureRecord<GateKey extends string> {
  attempt: number;
  gate: WorkspaceGateResult<GateKey>;
  /** `true` for the failure the tracked run is showing right now. */
  live: boolean;
}

/**
 * Every attempt that failed the same way, as one record.
 *
 * A repair loop that keeps tripping the same guard produces the same refusal
 * once per attempt. Listed one per attempt, the reader sees the identical
 * headline three times and has to open each to learn they match; grouped, the
 * run reads as "this check has refused every attempt" and the text appears
 * once.
 */
export interface GateFailureGroup<GateKey extends string> {
  gate: WorkspaceGateResult<GateKey>;
  detail: GateDetail;
  /** Ascending attempt numbers that failed this way. */
  attempts: number[];
  /** `true` when the tracked run's own failure is in this group. */
  live: boolean;
}

const failureSignature = <GateKey extends string>(
  gate: WorkspaceGateResult<GateKey>,
  detail: GateDetail
): string => `${gate.gateKey} ${normalize(detail.body ?? detail.summary)}`;

/**
 * Refused attempts folded by (gate, message), newest group first.
 *
 * Newest first because the live failure is what the reader is chasing; the
 * earlier, different refusals are context and follow it. Within a group the
 * attempts stay ascending so the label reads as a timeline.
 */
export const groupGateFailures = <GateKey extends string>(
  records: readonly GateFailureRecord<GateKey>[],
  config: WorkspaceGatesConfig<GateKey>
): GateFailureGroup<GateKey>[] => {
  const groups = new Map<string, GateFailureGroup<GateKey>>();
  for (const record of [...records].sort((a, b) => a.attempt - b.attempt)) {
    const detail = describeGateDetail(record.gate, config);
    const key = failureSignature(record.gate, detail);
    const existing = groups.get(key);
    if (existing) {
      existing.attempts.push(record.attempt);
      existing.live ||= record.live;
      // The newest record's gate carries the freshest structured details.
      existing.gate = record.gate;
      existing.detail = detail;
    } else {
      groups.set(key, {
        gate: record.gate,
        detail,
        attempts: [record.attempt],
        live: record.live,
      });
    }
  }
  const latest = (group: GateFailureGroup<GateKey>) =>
    group.attempts[group.attempts.length - 1];
  return [...groups.values()].sort((a, b) => latest(b) - latest(a));
};

/** "Initial checks, attempt 1, attempt 2" — the group's attempts as a phrase. */
export const attemptsPhrase = (attempts: readonly number[]): string =>
  attempts
    .map((attempt, index) => {
      const label = attemptLabel(attempt);
      return index === 0 ? label : label.toLowerCase();
    })
    .join(", ");
